import { tabKeyboard } from '@/lib/tabKeyboard';
// ----- Phase 5.25 — Import 3-tab page ----- //
//
// V1 parity: `src/screens/ImportScreen.jsx` has three tabs:
//   1. "Upload XML Files"        — parses eCTD XML backbones via
//                                  POST /api/import/parse-xml
//   2. "Upload eCTD ZIP Package" — extracts a full eCTD ZIP via
//                                  POST /api/import/parse-zip
//   3. "FDA ESG Lookup"          — fetches public submission history
//                                  from openFDA via GET /api/import/fda-esg/:nda
//
// All three converge on a shared metadata-confirmation panel that POSTs
// to /api/import/commit to create the submission rows.
//
// As of Phase 5.25.3, all three tabs are wired end-to-end. The
// `ImportComingSoonPlaceholder` is no longer rendered but the
// component is retained — keeps the page resilient if a future tab
// is added with a similar placeholder pattern (cheap to keep; one
// dead component, ~30 lines).

import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { XmlTab } from '@/components/import/XmlTab';
import { ZipTab } from '@/components/import/ZipTab';
import { FdaTab } from '@/components/import/FdaTab';

type TabKey = 'xml' | 'zip' | 'fda';

interface TabDescriptor {
  readonly key: TabKey;
  readonly label: string;
  readonly icon: string;
  readonly heading: string;
  readonly description: string;
}

const TABS: readonly TabDescriptor[] = [
  {
    key: 'xml',
    label: 'Upload XML Files',
    icon: '📄',
    heading: 'Upload eCTD XML Backbone Files',
    description:
      'Upload index.xml, regional XML, or EU envelope files. Parsing extracts sequence metadata, document references, and lifecycle keys for review before committing.',
  },
  {
    key: 'zip',
    label: 'Upload eCTD ZIP Package',
    icon: '📦',
    heading: 'Upload eCTD Submission Package',
    description:
      'Upload a complete eCTD submission as a ZIP file. Review the detected XML, documents and sequence metadata before confirming the import.',
  },
  {
    key: 'fda',
    label: 'Public FDA Lookup',
    icon: '🔎',
    heading: 'Look Up FDA Submission History',
    description:
      'Enter an NDA, ANDA, or BLA number to fetch publicly available submission history from openFDA. Public records are for reference only and cannot establish eCTD sequence identities. This lookup does not connect to the ESG gateway or confirm delivery.',
  },
];

export function ImportPage(): ReactElement {
  const [activeTab, setActiveTab] = useState<TabKey>('xml');

  const tab = TABS.find((t) => t.key === activeTab) ?? TABS[0]!;

  return (
    <section>
      <Link to="/home" style={{ color: 'var(--accent)' }}>
        ← Back to dashboard
      </Link>

      {/* phase-v1-ui-parity — V1 ImportScreen: .screen-title/.screen-subtitle
          classes, V1 subtitle copy (no trailing period). */}
      <h1 className="screen-title" style={{ marginTop: 12 }}>
        Import Existing Sequences
      </h1>
      <div className="screen-subtitle" style={{ marginBottom: 20 }}>
        Create draft sequence metadata from XML or ZIP, or look up public FDA history. Document files are not imported.
      </div>

      {/* ----- Tabs ----- */}
      <div
        role="tablist" onKeyDown={tabKeyboard}
        aria-label="Import method"
        data-testid="import-tabs"
        style={{
          display: 'flex',
          gap: 4,
          borderBottom: '1px solid #E5E7EB',
          marginBottom: 0,
        }}
      >
        {TABS.map((t) => {
          const isActive = t.key === activeTab;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`import-tab-${t.key}`}
              data-testid={`import-tab-${t.key}`}
              aria-selected={isActive} tabIndex={(isActive) ? 0 : -1}
              aria-controls={`import-tabpanel-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: '10px 18px',
                background: isActive ? '#fff' : 'transparent',
                border: 'none',
                borderBottom: isActive ? '2px solid var(--accent, #2563EB)' : '2px solid transparent',
                color: isActive ? 'var(--accent, #2563EB)' : '#6B7280',
                fontSize: 14,
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                marginBottom: -1,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ----- Active tab panel ----- */}
      <div
        role="tabpanel"
        id={`import-tabpanel-${tab.key}`}
        data-testid={`import-tabpanel-${tab.key}`}
        aria-labelledby={`import-tab-${tab.key}`}
        style={{
          background: '#fff',
          border: '1px solid #E5E7EB',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          padding: 24,
          marginBottom: 16,
        }}
      >
        <h2 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>{tab.heading}</h2>
        <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 20 }}>{tab.description}</p>

        {/* All three tabs wired. XML — Phase 5.25.1; ZIP — Phase
            5.25.2; FDA — Phase 5.25.3. */}
        {tab.key === 'xml' ? <XmlTab /> : tab.key === 'zip' ? <ZipTab /> : <FdaTab />}
      </div>

      <p
        data-testid="import-page-gap-note"
        style={{
          fontSize: 12,
          color: '#6B7280',
          fontStyle: 'italic',
        }}
      >
        Review imported metadata before use. Upload and verify the document files separately.
      </p>
    </section>
  );
}
